import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Security,
  Block,
  Warning,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Shield,
  Gavel
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { GuardLLMLogoShield } from '../components/GuardLLMLogoVariants';

interface DashboardStats {
  summary: {
    total_prompts: number;
    blocked_prompts: number;
    flagged_prompts: number;
    allowed_prompts: number;
    block_rate: string;
    avg_threat_score: number;
    total_tokens_used: number;
  };
  recent_threats: Array<{
    id: number;
    prompt_preview: string;
    threat_score: number;
    threat_types: string[];
    created_at: string;
  }>;
}

interface ComplianceFramework {
  name: string;
  display_name: string;
  security_rules: Array<any>;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [frameworks, setFrameworks] = useState<ComplianceFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardStats();
      fetchComplianceFrameworks();
    }
  }, [isAuthenticated]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
      // Set default data for MVP
      setStats({
        summary: {
          total_prompts: 0,
          blocked_prompts: 0,
          flagged_prompts: 0,
          allowed_prompts: 0,
          block_rate: '0.0%',
          avg_threat_score: 0.0,
          total_tokens_used: 0
        },
        recent_threats: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceFrameworks = async () => {
    try {
      const response = await axios.get('/api/compliance/frameworks');
      setFrameworks(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch compliance frameworks:', err);
      setFrameworks([]);
    }
  };

  const getTotalRulesCount = () => {
    return frameworks.reduce((sum, fw) => sum + (fw.security_rules?.length || 0), 0);
  };

  const getFrameworkIcon = (name: string) => {
    switch (name) {
      case 'HIPAA':
        return <Shield fontSize="small" />;
      case 'GDPR':
        return <Gavel fontSize="small" />;
      case 'SOC2':
        return <Security fontSize="small" />;
      case 'ISO27001':
        return <Shield fontSize="small" />;
      case 'NIST80053':
        return <Security fontSize="small" />;
      default:
        return <Security fontSize="small" />;
    }
  };

  const getThreatColor = (score: number) => {
    if (score >= 0.8) return 'error';
    if (score >= 0.5) return 'warning';
    return 'success';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={56} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GuardLLMLogoShield height={32} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.025em'
                }}
              >
                Security Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Real-time monitoring and security analytics
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={fetchDashboardStats}
              sx={{
                background: 'linear-gradient(135deg, #eef2ff 0%, #fce7f3 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e0e7ff 0%, #fce7f3 100%)'
                }
              }}
            >
              <RefreshIcon sx={{ color: '#6366f1' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 3,
            border: '1px solid #dbeafe'
          }}
          icon={<InfoIcon />}
        >
          {error} - Showing demo data for MVP
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card
            className="dashboard-card"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              position: 'relative',
              overflow: 'visible'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Security sx={{ fontSize: 28, color: '#6366f1' }} />
                  </Box>
                  <Chip
                    label="All Time"
                    size="small"
                    sx={{
                      background: '#f1f5f9',
                      fontWeight: 600,
                      fontSize: '0.688rem'
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Total Prompts
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#1e293b',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {stats?.summary.total_prompts.toLocaleString() || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            className="dashboard-card"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Block sx={{ fontSize: 28, color: '#dc2626' }} />
                  </Box>
                  <TrendingDown sx={{ color: '#dc2626' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Blocked Prompts
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#dc2626',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {stats?.summary.blocked_prompts.toLocaleString() || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            className="dashboard-card"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Warning sx={{ fontSize: 28, color: '#d97706' }} />
                  </Box>
                  <TrendingUp sx={{ color: '#d97706' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Flagged Prompts
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#d97706',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {stats?.summary.flagged_prompts.toLocaleString() || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            className="dashboard-card"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 28, color: '#059669' }} />
                  </Box>
                  <TrendingUp sx={{ color: '#059669' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Allowed Prompts
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#059669',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {stats?.summary.allowed_prompts.toLocaleString() || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance Security Rules Widget */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            className="dashboard-card"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
              height: '100%'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Shield sx={{ fontSize: 28, color: '#9333ea' }} />
                  </Box>
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      background: '#f3e8ff',
                      color: '#9333ea',
                      fontWeight: 600,
                      fontSize: '0.688rem'
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Security Rules
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#9333ea',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {getTotalRulesCount()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Across {frameworks.length} frameworks
                  </Typography>
                </Box>

                {frameworks.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <List dense sx={{ p: 0 }}>
                      {frameworks.slice(0, 3).map((fw) => (
                        <ListItem key={fw.name} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {getFrameworkIcon(fw.name)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, fontSize: '0.813rem' }}
                                >
                                  {fw.name}
                                </Typography>
                                <Chip
                                  label={`${fw.security_rules?.length || 0} rules`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.688rem',
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    {frameworks.length > 3 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        +{frameworks.length - 3} more frameworks
                      </Typography>
                    )}
                  </Box>
                )}

                <Button
                  component={Link}
                  to="/security-rules"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    mt: 2,
                    borderColor: '#e9d5ff',
                    color: '#9333ea',
                    '&:hover': {
                      borderColor: '#9333ea',
                      background: '#faf5ff'
                    }
                  }}
                >
                  View All Rules
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Metrics */}
        <Grid item xs={12} md={6}>
          <Card className="dashboard-card" sx={{ height: '100%' }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 3
                }}
              >
                Security Metrics
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Block Rate
                    </Typography>
                    <Chip
                      label={stats?.summary.block_rate || '0.0%'}
                      size="small"
                      sx={{
                        background:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                            : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        color:
                          parseFloat(stats?.summary.block_rate || '0') > 10 ? '#dc2626' : '#059669',
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor:
                          parseFloat(stats?.summary.block_rate || '0') > 10 ? '#f87171' : '#6ee7b7'
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(stats?.summary.block_rate || '0')}
                    sx={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': {
                        background:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                            : 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Average Threat Score
                    </Typography>
                    <Chip
                      label={(Number(stats?.summary.avg_threat_score) || 0).toFixed(2)}
                      size="small"
                      sx={{
                        background:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                              : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        color:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? '#dc2626'
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? '#d97706'
                              : '#059669',
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? '#f87171'
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? '#fbbf24'
                              : '#6ee7b7'
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(Number(stats?.summary.avg_threat_score) || 0) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': {
                        background:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                              : 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                      }
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5 }}>
                    Total Tokens Used
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: '#1e293b',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {(stats?.summary.total_tokens_used || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Threats */}
        <Grid item xs={12} md={6}>
          <Card className="dashboard-card" sx={{ height: '100%' }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 3
                }}
              >
                Recent Threats
              </Typography>

              {stats?.recent_threats && stats.recent_threats.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {stats.recent_threats.slice(0, 5).map((threat, index) => (
                    <ListItem
                      key={threat.id}
                      divider={index < 4}
                      sx={{
                        px: 0,
                        py: 2,
                        '&:hover': {
                          background: '#f8fafc',
                          borderRadius: 2,
                          px: 2
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: '60%',
                                fontWeight: 600,
                                color: '#1e293b'
                              }}
                            >
                              {threat.prompt_preview}
                            </Typography>
                            <Chip
                              label={`${(Number(threat.threat_score) * 100 || 0).toFixed(0)}%`}
                              size="small"
                              sx={{
                                background:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                                      : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                color:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? '#dc2626'
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? '#d97706'
                                      : '#059669',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                border: '1px solid',
                                borderColor:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? '#f87171'
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? '#fbbf24'
                                      : '#6ee7b7'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.75}>
                              {threat.threat_types.slice(0, 3).map((type) => (
                                <Chip
                                  key={type}
                                  label={type}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.688rem',
                                    height: 22,
                                    borderColor: '#cbd5e1',
                                    color: '#64748b',
                                    fontWeight: 600
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.75rem'
                              }}
                            >
                              {formatDate(threat.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #a7f3d0'
                  }}
                >
                  <CheckCircle sx={{ fontSize: 48, color: '#059669', mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: '#065f46'
                    }}
                  >
                    No recent threats detected
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#059669', mt: 0.5 }}>
                    Your system is secure!
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
              border: '1px solid #e2e8f0'
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                mb: 1,
                letterSpacing: '-0.025em'
              }}
            >
              Quick Start Guide
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{ mb: 3, fontWeight: 500 }}
            >
              Welcome to NeuraStrike! Here's how to get started:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: '#e0e7ff',
                    background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#6366f1',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.2)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <ScienceIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#6366f1',
                        mb: 1.5,
                        letterSpacing: '-0.025em'
                      }}
                    >
                      1. Test Prompts
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.6
                      }}
                    >
                      Go to Prompt Tester to test your prompts for security threats and see
                      real-time analysis.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: '#fce7f3',
                    background: 'linear-gradient(135deg, #fce7f3 0%, #ffffff 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#ec4899',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 15px -3px rgba(236, 72, 153, 0.2)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#ec4899',
                        mb: 1.5,
                        letterSpacing: '-0.025em'
                      }}
                    >
                      2. Configure Rules
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.6
                      }}
                    >
                      Visit Security Rules to customize threat detection patterns and security
                      policies.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: '#dbeafe',
                    background: 'linear-gradient(135deg, #dbeafe 0%, #ffffff 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.2)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <AnalyticsIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#3b82f6',
                        mb: 1.5,
                        letterSpacing: '-0.025em'
                      }}
                    >
                      3. Monitor Activity
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.6
                      }}
                    >
                      Check Prompt History to review all processed prompts and their security
                      analysis.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
